// API Keys Service - MCP API key management API calls

import api from './api';
import type { ApiKey, ApiKeyCreated, MCPServer } from '../types/api';

interface ApiKeysListResponse {
  total: number;
  keys: ApiKey[];
}

interface MCPServersResponse {
  total: number;
  servers: MCPServer[];
}

interface CreateApiKeyRequest {
  name: string;
  role?: string;
  notes?: string;
  allowed_servers: string[];
}

interface UpdateApiKeyRequest {
  notes?: string;
}

export const apiKeysService = {
  /**
   * List all API keys
   */
  list: async (includeInactive: boolean = false): Promise<ApiKeysListResponse> => {
    const params = includeInactive ? '?include_inactive=true' : '';
    const response = await api.get<ApiKeysListResponse>(`/admin/api-keys${params}`);
    return response.data;
  },

  /**
   * Get a single API key by ID
   */
  get: async (keyId: string): Promise<ApiKey> => {
    const response = await api.get<ApiKey>(`/admin/api-keys/${keyId}`);
    return response.data;
  },

  /**
   * Create a new API key
   */
  create: async (data: CreateApiKeyRequest): Promise<ApiKeyCreated> => {
    const response = await api.post<ApiKeyCreated>('/admin/api-keys', data);
    return response.data;
  },

  /**
   * Update API key metadata (notes)
   */
  update: async (keyId: string, data: UpdateApiKeyRequest): Promise<ApiKey> => {
    const response = await api.patch<ApiKey>(`/admin/api-keys/${keyId}`, data);
    return response.data;
  },

  /**
   * Deactivate an API key
   */
  deactivate: async (keyId: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/admin/api-keys/${keyId}`);
    return response.data;
  },

  /**
   * Reactivate a deactivated API key
   */
  reactivate: async (keyId: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/admin/api-keys/${keyId}/reactivate`);
    return response.data;
  },

  /**
   * List available MCP servers
   */
  getMcpServers: async (): Promise<MCPServersResponse> => {
    const response = await api.get<MCPServersResponse>('/admin/mcp-servers');
    return response.data;
  },
};
